import React, { useEffect, useState } from 'react'

import Modal from '../components/modal'
import config from '../config.json'
import ScreenHelpButton from '../modules/help/ScreenHelpButton'
import {
  deletePasskey,
  getDefaultDeviceName,
  isWebAuthnSupported,
  listPasskeys,
  registerPasskey,
} from '../login/webauthn'

const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('es-AR')
}

const PasskeysModal = ({ onClose, Alert }) => {
  const [credentials, setCredentials] = useState([])
  const [loading, setLoading] = useState(true)
  const [registering, setRegistering] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [error, setError] = useState(null)

  const loadCredentials = () => {
    setLoading(true)
    setError(null)

    listPasskeys({ apiUrl: config.apiUrl, token: config.token })
      .then((items) => setCredentials(items))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadCredentials()
  }, [])

  const handleRegister = () => {
    if (!isWebAuthnSupported()) {
      setError('Tu navegador no soporta biometria.')
      return
    }

    setRegistering(true)
    setError(null)

    registerPasskey({
      apiUrl: config.apiUrl,
      token: config.token,
      deviceName: getDefaultDeviceName(),
    })
      .then(() => {
        loadCredentials()
        Alert({
          title: 'Biometria',
          msg: 'Dispositivo biometrico registrado correctamente.',
          type: 'success',
          closeButton: true,
        })
      })
      .catch((err) => setError(err.message))
      .finally(() => setRegistering(false))
  }

  const handleDelete = (credentialId) => {
    setDeletingId(credentialId)
    setError(null)

    deletePasskey({
      apiUrl: config.apiUrl,
      token: config.token,
      credentialId,
    })
      .then(() => loadCredentials())
      .catch((err) => setError(err.message))
      .finally(() => setDeletingId(null))
  }

  return (
    <Modal
      title="Dispositivos biometricos"
      modalId="passkeys-modal"
      modalClose={onClose}
      Alert={Alert}
      width="480px"
    >
      <div className="passkeys-modal">
        <div className="passkeys-modal__intro-row">
          <p className="passkeys-modal-hint">
            Administre los dispositivos con huella o rostro vinculados a su usuario.
          </p>
          <ScreenHelpButton screenId="settings-passkeys" Alert={Alert} iconOnly />
        </div>

        {loading && <p className="passkeys-modal-status">Cargando…</p>}

        {!loading && credentials.length === 0 && (
          <p className="passkeys-modal-status">No hay dispositivos biometricos registrados.</p>
        )}

        {!loading && credentials.length > 0 && (
          <ul className="passkeys-list">
            {credentials.map((item) => (
              <li key={item.credential_id} className="passkeys-list-item">
                <div className="passkeys-list-info">
                  <span className="passkeys-list-name">{item.device_name || 'Dispositivo'}</span>
                  <span className="passkeys-list-meta">
                    Registrado: {formatDate(item.iat)}
                    {item.last_used ? ` · Ultimo uso: ${formatDate(item.last_used)}` : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className="passkeys-delete-btn"
                  onClick={() => handleDelete(item.credential_id)}
                  disabled={deletingId === item.credential_id || registering}
                >
                  {deletingId === item.credential_id ? 'Eliminando…' : 'Eliminar'}
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="passkeys-modal-error">{error}</p>}

        <div className="passkeys-modal-actions">
          <button type="button" className="modal-btn" onClick={onClose} disabled={registering}>
            Cerrar
          </button>
          <button
            type="button"
            className="modal-btn primary"
            onClick={handleRegister}
            disabled={registering || !isWebAuthnSupported()}
          >
            {registering ? 'Registrando…' : 'Registrar este dispositivo'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PasskeysModal
